import { Box, Card, CardContent, Skeleton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX } from "react";

import { SectionCardHeader, thoughtTint } from "../../shared.js";
import type { AgentThought } from "../../types.js";
import { agoFmt } from "./statusHelpers.js";

/** Live thinking feed — the agent's recent reasoning trace (refreshes every 5s). */
export function ThinkingFeed({
  thoughts,
}: {
  thoughts: AgentThought[] | undefined;
}): JSX.Element {
  return (
    <Card data-testid="status-thinking">
      <SectionCardHeader
        title="What it's thinking"
        subtitle="Live reasoning trace. Updates every 5 s."
      />
      <CardContent sx={{ px: 2.5, py: 2 }}>
        {!thoughts ? (
          <Skeleton variant="rounded" height={120} />
        ) : thoughts.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            No recent reasoning. The agent runs on a schedule — give it a
            minute, or steer it from the header.
          </Typography>
        ) : (
          <Box
            component="ul"
            data-testid="thinking-feed"
            sx={{
              m: 0,
              p: 0,
              listStyle: "none",
              display: "grid",
              gap: 0.75,
              // The trace can run to 25 single-line rows; cap it so it scrolls
              // in place instead of stretching this rail past the schedule.
              maxHeight: 360,
              overflowY: "auto",
            }}
          >
            {thoughts.slice(0, 25).map((t) => (
              <Box
                component="li"
                key={t.id}
                sx={{ display: "flex", alignItems: "baseline", gap: 1 }}
              >
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    width: 56,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 11,
                    color: "text.secondary",
                  }}
                >
                  {agoFmt(t.created_at)}
                </Box>
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.25,
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    bgcolor: (th) => alpha(thoughtTint(th, t.thought_type), 0.16),
                    color: (th) => thoughtTint(th, t.thought_type),
                  }}
                >
                  {t.thought_type}
                </Box>
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 14,
                  }}
                >
                  {t.title}
                </Box>
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 10,
                    color: "text.secondary",
                  }}
                >
                  {t.agent_name}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
